import { createSelector, PayloadAction, ThunkDispatch } from "@reduxjs/toolkit";
import { getCacheKey } from "../apiRTK";
import { createAppSlice } from "../app/createAppSlice";
import { IAppThunk, IRootState } from "../app/store";
import { pyceilingRTK } from "../pyCeiling/pyCeilingRTK";
import { IAdjustedCeiling, type IHimlingSpace, ISpaceTrainingDataInput } from "../types/types";
import {
  convertMcBimSpaceToPixPolygon,
  getCalculatedCeilingGrids,
  getInputForMlData,
  getMcbimSpacesFromFreeTextSearch,
  getRandomNrBetween,
  PartialMcBimSpace,
  roomHasCeilingCoordinates,
} from "../utils";
import { designRTK } from "./designRTK";
import { IPixPath, IPixPolygon } from "../components/PixBimViewer/types";
import { IfcClassesEnum } from "../enums";
import {
  getValidationsForSpace,
  ISpaceValidation,
  SPACE_VALIDATION_ID_ENUM,
} from "../RoomSideBar/SpaceValidations/spaceValidationsUtils";
import {
  COLOR_FILTERS_IDS,
  getPolygonColorsOfPixPolygonSpace,
} from "./LeftSidebar/ColorPanel/colorPanelUtils";
import {
  IDevice,
  IDeviceTypesIfcInstanceClassesEnum,
  IMcBimItem,
  IMLPredictDevice,
  IObjectId,
  IRoomsSummaryHashmap,
} from "@backend/src/schemas";
import { AccountInfo } from "@azure/msal-browser";
import {
  openapi,
  PostMultiplacerDevicesApiArg,
  useGetMultiplacerProjectsSingleProjectQuery,
} from "../../openapi";
import { convertGenereicMlResultToPixPolygon } from "./devices/deviceUtils";
import {
  selectHttpMcBimItemsInActiveStandardRoom,
  selectMcBimSpaceFromSelectedRoomNr,
} from "../McBim/McBimSlice";
import { selectHttpDevicesInRoom } from "./devices/deviceSlice";
import {
  selectGetRoomsArgs,
  selectHttpMcbimSpacesResult,
  selectMcbimSpaceHitSetByFreeTextSearch,
} from "../McBim/mcbimSelectors";
import { selectedSpaceNumber } from "./designSelectors";
import {
  selectActiveSingleRoomSummary,
  selectGetColorFilters,
  selectHttpGetRoomsCurrentFloorResult,
} from "../Room/roomSelectors";
import { IActiveModule } from "./GlobalDesignBar/designModules";
import { DesignLeftSideBarTabs, RoomSideBarTabs } from "./designEnums";
import { clearDesignThunk, IRoomMcBimSpaceCombo } from "./designThunk";
import { IPixBimCameraState } from "../components/PixBimViewer/PixBimViewer";

export enum ETab {
  DESIGN = "DESIGN",
}

interface IToast {
  header: string;
  text: string;
  isSuccess: boolean;
  isOpen: boolean;
}

interface ITrainAll {
  loading: boolean;
  nrOfTrainedRooms: number;
  nrOfFailedRooms: number;
}

export interface ISpaceNumberAndSpaceValidationsResults {
  spaceNumber: string;
  spaceValidations: ISpaceValidation[];
}

export interface ISpaceValidationRun {
  active: boolean;
  loading: boolean;
  spacesValidationsResults: ISpaceNumberAndSpaceValidationsResults[];
}
export type IDeviceViewMode = "devices" | "ifc";

export type IFloorAndJob = {
  floorName: string;
  floorGlobalId: string;
  jobId: string;
} | null;

export interface IBuildingStoryListState {
  search: string;
  sortMode: "name" | "rooms";
}

export type IfcClassesToDisplayMap = Partial<Record<IfcClassesEnum, boolean>>;

export type IRfpStandardRoomDiscipline = "standardRoom.E" | "standardRoom.V" | undefined;

export interface IDesignSliceState {
  activeModule: IActiveModule;
  accountInfo: (AccountInfo & { accessToken: string }) | null;
  floorAndJob: IFloorAndJob;
  value: number;
  projectNo: number;
  activeTab: ETab;
  mlGenrericSpaceDevices: IMLPredictDevice[];
  algosInProgress: boolean;
  countAlgosSpace: {
    total: number;
    complete: number;
    failed: number;
  };
  highLightStandardSpaces: boolean;
  selectedSpaceNumber: string | undefined;
  copySpaceModeActive: boolean;
  selectedRoomTab: RoomSideBarTabs | undefined;
  saveEditedRoomsLoading: boolean;
  toast: IToast;
  trainAll: ITrainAll;
  fillTile: boolean;
  hidePlayButton: boolean;
  hideApp: boolean;
  allowMultiselect: boolean;
  viewerToBeShow: "FLOORVIEWER" | "SINGLEVIEWER";
  hideDevices: boolean;
  searchTerm: string;
  selectedSpaceValidation: SPACE_VALIDATION_ID_ENUM | null;
  activeLeftSideBarTab: DesignLeftSideBarTabs | undefined;
  dragSelectSpaces: boolean;
  selectedRooms: string[];
  shiftSelectRooms: boolean;
  review: {
    active: boolean;
    currIndex: number;
  };
  ifcClassesToDisplay: IfcClassesToDisplayMap;
  deviceTypeInstanceClassFilter: IDeviceTypesIfcInstanceClassesEnum;
  rotation: number;
  showTrueSizeOfDevices: boolean;
  snapToGrid: boolean;
  spaceValidationRun: ISpaceValidationRun;
  activeSpaceValidationSpecs: Record<SPACE_VALIDATION_ID_ENUM, boolean>;
  activeColorPanelItem: COLOR_FILTERS_IDS | string | undefined;
  freeTextSearch: string;
  showFireDeviceCircles: boolean;
  showFireDeviceSquare: boolean;
  deviceOnTheMove: IObjectId | undefined;
  measurementToolActive: boolean;
  nonPublischedDevicesFromAlgos: IDevice[];
  deviceViewMode: IDeviceViewMode; // nytt
  buildingStoryList: IBuildingStoryListState;
  showColorFilterLarge: boolean;
  rfpStandardRoomDiscipline: IRfpStandardRoomDiscipline;
  zoomExtentNonce: number;
  mouseHelpModalOpen: boolean;
}

const initialState: IDesignSliceState = {
  activeModule: undefined,
  accountInfo: null,
  floorAndJob: null,
  value: 0,
  projectNo: 0,
  activeTab: (localStorage.getItem("activeTab") as ETab) || ETab.DESIGN,
  mlGenrericSpaceDevices: [],
  algosInProgress: false,
  countAlgosSpace: {
    total: 0,
    complete: 0,
    failed: 0,
  },
  highLightStandardSpaces: false,
  selectedSpaceNumber: undefined, // "2iq$n4e1nCrhzyqoV6EG$J",
  copySpaceModeActive: false,
  selectedRoomTab:
    (localStorage.getItem("roomSideBarTab") as RoomSideBarTabs) || RoomSideBarTabs.ROOM_INFO,
  saveEditedRoomsLoading: false,
  toast: {
    header: "",
    text: "",
    isSuccess: false,
    isOpen: false,
  },
  trainAll: {
    loading: false,
    nrOfTrainedRooms: 0,
    nrOfFailedRooms: 0,
  },
  fillTile: false,
  hidePlayButton: false,
  hideApp: false,
  allowMultiselect: false,
  viewerToBeShow: "FLOORVIEWER",
  hideDevices: false,
  searchTerm: "",
  selectedSpaceValidation: null,
  activeLeftSideBarTab: undefined,
  dragSelectSpaces: false,
  selectedRooms: [],
  shiftSelectRooms: false,
  review: {
    active: false,
    currIndex: 0,
  },
  ifcClassesToDisplay: {
    [IfcClassesEnum.IFCFURNITURE]: true,
    [IfcClassesEnum.IFCCOLUMN]: false,
  },
  deviceTypeInstanceClassFilter: IfcClassesEnum.IFCAIRTERMINAL,
  rotation: (Number(localStorage.getItem("rotation")) as number) || 0,
  showTrueSizeOfDevices: false,
  snapToGrid: true,
  spaceValidationRun: {
    loading: false,
    active: false,
    spacesValidationsResults: [],
  },
  activeSpaceValidationSpecs: Object.values(SPACE_VALIDATION_ID_ENUM).reduce(
    (acc, value) => {
      acc[value] = true;
      return acc;
    },
    {} as Record<SPACE_VALIDATION_ID_ENUM, boolean>
  ),
  activeColorPanelItem: undefined,
  freeTextSearch: "",
  showFireDeviceCircles: false,
  showFireDeviceSquare: false,
  deviceOnTheMove: undefined,
  measurementToolActive: false,
  nonPublischedDevicesFromAlgos: [],
  deviceViewMode: (localStorage.getItem("deviceViewMode") as IDeviceViewMode) || "devices",
  buildingStoryList: {
    search: "",
    sortMode: "name",
  },
  showColorFilterLarge: true,
  rfpStandardRoomDiscipline: localStorage.getItem("rfpStandardRoomDiscipline") as any,
  zoomExtentNonce: 0,
  mouseHelpModalOpen: false,
};

interface IFetchItemsArgs {
  standardSpaceRoomNr: string;
  roomMcBimSpace: IRoomMcBimSpaceCombo;
}

const fetchStandardSpaceAndItems = async (
  dispatch: ThunkDispatch<IRootState, unknown, PayloadAction<any>>,
  getState: () => IRootState,
  args: IFetchItemsArgs
) => {
  const floorAndJob = getState().design.floorAndJob;
  if (floorAndJob == null) {
    return { standardSpace: null, mcbimItemsInStandardSpace: [], mcbimSpace: null };
  }
  // Hämta standardSpace
  const promiseStandardSpace = dispatch(
    openapi.endpoints.getMcbimSingleMcbimSpace.initiate({
      projectNo: getState().design.projectNo,
      name: args.standardSpaceRoomNr,
    })
  );
  const { data: standardSpace } = await promiseStandardSpace;
  if (standardSpace == null) {
    dispatch(setCountAlgoSpaceFailed(getState().design.countAlgosSpace.failed + 1));
    return { standardSpace: null, mcbimItemsInStandardSpace: [], mcbimSpace: null };
  }

  // Hämta items i standardSpace
  const promiseMcBimItemsInStandardSpace = dispatch(
    openapi.endpoints.getMcbimItemsInSpace.initiate({
      projectNo: getState().design.projectNo,
      spaceId: standardSpace._id,
    })
  );
  const { data: mcbimItemsInStandardSpace = [] } = await promiseMcBimItemsInStandardSpace;

  // Hämta mcbimSpace
  const promiseMcBimSpace = dispatch(
    openapi.endpoints.getMcbimSingleMcbimSpace.initiate({
      projectNo: getState().design.projectNo,
      name: args.roomMcBimSpace.roomNumber,
    })
  );
  const { data: mcbimSpace = [] } = await promiseMcBimSpace;

  return { standardSpace, mcbimItemsInStandardSpace, mcbimSpace };
};

export const runFetchStandardSpaceAndItemsForMultiple = (
  args: IFetchItemsArgs[],
  dispatch: ThunkDispatch<IRootState, unknown, PayloadAction<any>>,
  getState: () => IRootState
) => {
  // TODO: Implement function logic or remove if not needed
  return async () => {
    const chunkSize = 40;
    const results: any[] = [];
    for (let i = 0; i < args.length; i += chunkSize) {
      const chunk = args.slice(i, i + chunkSize);
      const chunkResults = await Promise.all(
        chunk.map((arg) => fetchStandardSpaceAndItems(dispatch, getState, arg))
      );
      results.push(...chunkResults);
    }
    return results;
  };
};

interface IToastArgs {
  text: string;
  header: string;
  isSuccess: boolean;
}

export const thunkToast = (args: IToastArgs): IAppThunk => {
  return (dispatch) => {
    dispatch(
      setToastInRedux({
        text: args.text,
        header: args.header,
        isOpen: true,
        isSuccess: args.isSuccess,
      })
    );
    setTimeout(() => {
      dispatch(
        setToastInRedux({
          text: "",
          header: "",
          isOpen: false,
          isSuccess: args.isSuccess,
        })
      );
    }, 5000);
  };
};

export const thunkChangeStepInReviewMode = (inc: boolean): IAppThunk => {
  return (dispatch, getState) => {
    const { currIndex, active } = getState().design.review;
    if (!active) {
      return;
    }
    const selectedRooms = getState().design.selectedRooms;

    if (inc) {
      const isOnLast = currIndex + 1 === selectedRooms.length;
      if (isOnLast) {
        return;
      }
      dispatch(incCurrIndexReviewModeInRedux());
    } else {
      if (currIndex === 0) {
        return;
      }
      dispatch(decCurrIndexReviewModeInRedux());
    }
    dispatch(setSelectedSpaceNumberInRedux(selectedRooms[currIndex + (inc ? 1 : -1)]));
  };
};

export interface ITrainAllArgs {
  limit: number;
  onlyMissing: boolean;
  prevTraindStandardSpaces: string[];
  standardSpaces: string[];
}

export const thunkPublishAlgoDevices = (): IAppThunk => {
  return async (dispatch, getState) => {
    const args = selectGetRoomsArgs(getState());
    if (args == null) {
      return;
    }
    const roomRtk = openapi.endpoints.postMultiplacerRooms.select(args)(getState());
    const rooms = roomRtk.data as IRoomsSummaryHashmap;
    const devices = getState().design.nonPublischedDevicesFromAlgos;
    const formattedDevices = devices.map((device) => {
      const room = rooms[device.roomNumber];
      const revitCeilingHeight = room.ceilingCoordinate ? room.ceilingCoordinate[2] : undefined;
      let copyOfDevice = structuredClone(device);
      copyOfDevice.revitCeilingHeight = revitCeilingHeight;
      delete copyOfDevice._id;
      delete copyOfDevice.boundingBox;
      return copyOfDevice;
    });
    dispatch(
      openapi.endpoints.postMultiplacerDeviceAddDevices.initiate({
        body: {
          devices: formattedDevices,
        },
      })
    );
    dispatch(clearDesignThunk());

    dispatch(thunkToast({ text: "Devices copied", header: "Success", isSuccess: true }));
  };
};

export const thunkRunSpaceValidations = (changeTab = true): IAppThunk => {
  return async (dispatch, getState) => {
    const floorAndJob = getState().design.floorAndJob;
    if (floorAndJob == null) {
      return;
    }
    dispatch(setSpaceValidationLoadingInRedux(true));
    dispatch(setSpaceValidationRunActiveInRedux(true));
    const queries = getState().api.queries;
    const cacheKeyGetRooms = getCacheKey(queries, "getMultiplacerRooms");
    const respGetRooms = openapi.endpoints.postMultiplacerRooms.select(cacheKeyGetRooms);
    const rooms = respGetRooms(getState()).data as IRoomsSummaryHashmap;
    const activeSpaceValidationSpecs = getState().design.activeSpaceValidationSpecs;

    const cacheKeyGetSpaces = getCacheKey(queries, "getMcbimSpaces");
    const respGetSpaces = openapi.endpoints.getMcbimSpaces.select(cacheKeyGetSpaces);
    const mcbimSpaces = respGetSpaces(getState()).data as PartialMcBimSpace[];

    const respGetDevices = openapi.endpoints.postMultiplacerDevices.select(
      getCacheKey(queries, "postMultiplacerDevices")
    );

    const respGetProject = openapi.endpoints.getMultiplacerProjectsSingleProject.select(
      getCacheKey(queries, "getMultiplacerProjectsSingleProject")
    );

    const project = respGetProject(getState()).data;

    if (project == null) {
      return;
    }
    const devices = respGetDevices(getState()).data as IDevice[];

    const selectedRooms = getState().design.selectedRooms;

    const spaceValidations: ISpaceNumberAndSpaceValidationsResults[] = [];

    for (const roomNumber of selectedRooms) {
      let standardSpacItems: IMcBimItem[] = [];
      const room = rooms[roomNumber];
      const mcbimSpace = mcbimSpaces.find(
        (space) => space.name === roomNumber
      ) as PartialMcBimSpace;
      const devicesInRoom = devices.filter((device) => device.roomNumber === roomNumber);

      if (room?.rfpMalRom != null) {
        const promiseMcBimSpaceStandardRoom = dispatch(
          openapi.endpoints.getMcbimSingleMcbimSpace.initiate({
            projectNo: project.projectNo,
            name: room.rfpMalRom,
          })
        );
        let { data: mcbimSpaceStandardRoom } = await promiseMcBimSpaceStandardRoom;
        if (mcbimSpaceStandardRoom != null) {
          const promiseMcBimItemsInStandardRoom = dispatch(
            openapi.endpoints.getMcbimItemsInSpace.initiate({
              projectNo: project.projectNo,
              spaceId: mcbimSpaceStandardRoom._id as string,
            })
          );
          const { data: items } = await promiseMcBimItemsInStandardRoom;
          standardSpacItems = items as IMcBimItem[];
        }
      }

      if (room != null && mcbimSpace != null && devicesInRoom.length > 0) {
        const validations = getValidationsForSpace({
          devicesInRoom,
          singleRoom: room,
          mcbimItemsInStandardSpace: standardSpacItems,
          singleSpace: mcbimSpace,
          activeSpaceValidationSpecs,
          compCodePath: project.compCodePath,
        });
        spaceValidations.push({
          spaceNumber: roomNumber,
          spaceValidations: validations,
        });
      }
    }
    dispatch(setSpaceValidationsResultsInRedux(spaceValidations));
    if (changeTab) {
      dispatch(setActiveLeftSideBarTabInRedux(DesignLeftSideBarTabs.CEILING_VALIDATIONS));
    }
    dispatch(setSpaceValidationLoadingInRedux(false));
  };
};

export const thunkAddFreeTextResultsToSelectedRooms = (): IAppThunk => {
  return (dispatch, getState) => {
    const queries = getState().api.queries;
    const cacheKey = getCacheKey(queries, "getMcBimSpaces");
    const resp = openapi.endpoints.getMcbimSpaces.select(cacheKey);
    const mcbimSpaces = resp(getState()).data as PartialMcBimSpace[];

    const freeTextSearch = getState().design.freeTextSearch;
    const selectedRooms = getState().design.selectedRooms;
    const resultsInFreeTextSearch = getMcbimSpacesFromFreeTextSearch({
      mcbimSpaces,
      freeTextSearch,
      output: "MCBIMITEMS",
    }) as IMcBimItem[];

    const newSelectedRooms = resultsInFreeTextSearch?.filter((item) => {
      return selectedRooms.includes(item.name) === false;
    });
    if (newSelectedRooms == null) {
      return;
    }
    dispatch(
      setSelectedRoomsInRedux([...selectedRooms, ...newSelectedRooms.map((item) => item.name)])
    );
    dispatch(setFreeTextSearchInRedux(""));
  };
};

export const thunkStartReviewProcess = (devices: IDevice[]): IAppThunk => {
  return (dispatch, getState) => {
    const selectedRooms = getState().design.selectedRooms;
    if (selectedRooms.length === 0) {
      return;
    }
    const roomsWithDevices = selectedRooms.filter((roomNumber) => {
      return devices.find((device) => device.roomNumber === roomNumber) != null;
    });
    dispatch(setSelectedRoomsInRedux(roomsWithDevices));
    dispatch(setSelectedSpaceNumberInRedux(selectedRooms[0]));
    dispatch(toggleReviewModeInRedux(true));
  };
};

// If you are not using async thunks you can use the standalone `createSlice`.
export const designSlice = createAppSlice({
  name: "design",
  initialState,
  reducers: () => ({
    setFloorAndJobInRedux: (state, action: PayloadAction<IFloorAndJob | null>) => {
      state.floorAndJob = action.payload;
    },
    setActiveTabInRedux: (state, action: PayloadAction<ETab>) => {
      //spara detta i localstorge
      localStorage.setItem("activeTab", action.payload);
      state.activeTab = action.payload;
    },

    setCountAlgoSpaceTotal: (state, action: PayloadAction<number>) => {
      state.countAlgosSpace.total = action.payload;
    },
    setCountAlgoSpaceComplete: (state, action: PayloadAction<number>) => {
      state.countAlgosSpace.complete = action.payload;
    },
    setCountAlgoSpaceFailed: (state, action: PayloadAction<number>) => {
      state.countAlgosSpace.failed = action.payload;
    },
    setAlgosInProgressInRedux: (state, action: PayloadAction<boolean>) => {
      state.algosInProgress = action.payload;
    },
    setHighlightStandardSpacesInRedux: (state, action: PayloadAction<boolean>) => {
      state.highLightStandardSpaces = action.payload;
    },
    setSelectedSpaceNumberInRedux: (state, action: PayloadAction<string | undefined>) => {
      state.selectedSpaceNumber = action.payload;
    },
    setToastInRedux: (state, action: PayloadAction<IToast>) => {
      state.toast = action.payload;
    },
    setTrainAllLoadingInRedux: (state, action: PayloadAction<ITrainAll>) => {
      state.trainAll = action.payload;
    },
    setFillTileInRedux: (state, action: PayloadAction<boolean>) => {
      state.fillTile = action.payload;
    },
    setHidePlayButtonInRedux: (state, action: PayloadAction<boolean>) => {
      state.hidePlayButton = action.payload;
    },
    setHideAppInRedux: (state, action: PayloadAction<boolean>) => {
      state.hideApp = action.payload;
    },
    setAllowMultiselectInRedux: (state, action: PayloadAction<boolean>) => {
      state.allowMultiselect = action.payload;
    },
    setViewerToBeShowInRedux: (state, action: PayloadAction<"FLOORVIEWER" | "SINGLEVIEWER">) => {
      state.viewerToBeShow = action.payload;
    },
    setHideDevicesInRedux: (state, action: PayloadAction<boolean>) => {
      state.hideDevices = action.payload;
    },
    setSearchTermInRedux: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    setSelectedRoomTabInRedux: (state, action: PayloadAction<RoomSideBarTabs | undefined>) => {
      state.selectedRoomTab = action.payload;
    },
    setSelectedSpaceValidationInRedux: (
      state,
      action: PayloadAction<SPACE_VALIDATION_ID_ENUM | null>
    ) => {
      state.selectedSpaceValidation = action.payload;
    },
    setActiveLeftSideBarTabInRedux: (
      state,
      action: PayloadAction<DesignLeftSideBarTabs | undefined>
    ) => {
      state.activeLeftSideBarTab = action.payload;
    },
    setDragSelectSpacesInRedux: (state, action: PayloadAction<boolean>) => {
      state.dragSelectSpaces = action.payload;
    },
    setSelectedRoomsInRedux: (state, action: PayloadAction<string[]>) => {
      state.selectedRooms = action.payload;
    },
    addSelectedRoomToRedux: (state, action: PayloadAction<string>) => {
      state.selectedRooms.push(action.payload);
    },
    setMultipleSelectedRoomsToRedux: (state, action: PayloadAction<string[]>) => {
      state.selectedRooms = action.payload;
    },
    removeSelectedRoomFromRedux: (state, action: PayloadAction<string>) => {
      state.selectedRooms = state.selectedRooms.filter((room) => room !== action.payload);
    },
    setShiftSelectRoomsInRedux: (state, action: PayloadAction<boolean>) => {
      state.shiftSelectRooms = action.payload;
    },
    toggleReviewModeInRedux: (state, action: PayloadAction<boolean>) => {
      state.review.active = action.payload;
    },
    incCurrIndexReviewModeInRedux: (state) => {
      state.review.currIndex++;
    },
    decCurrIndexReviewModeInRedux: (state) => {
      state.review.currIndex--;
    },
    resetReviewModeInRedux: (state) => {
      state.review.active = false;
      state.review.currIndex = 0;
    },
    setIfcClassesToDisplayInRedux: (state, action: PayloadAction<IfcClassesEnum>) => {
      state.ifcClassesToDisplay[action.payload] = !state.ifcClassesToDisplay[action.payload];
    },
    setDeviceTypeInstanceClassFilterInRedux: (
      state,
      action: PayloadAction<IDeviceTypesIfcInstanceClassesEnum>
    ) => {
      state.deviceTypeInstanceClassFilter = action.payload;
    },
    setRotationInRedux: (state, action: PayloadAction<number>) => {
      localStorage.setItem("rotation", action.payload.toString());
      state.rotation = action.payload;
    },
    setCopySpaceModeActiveInRedux: (state, action: PayloadAction<boolean>) => {
      state.copySpaceModeActive = action.payload;
    },
    toggleShowTrueSizeOfDevicesInRedux: (state) => {
      state.showTrueSizeOfDevices = !state.showTrueSizeOfDevices;
    },
    toggleSnapToGridInRedux: (state) => {
      state.snapToGrid = !state.snapToGrid;
    },
    setSpaceValidationRunActiveInRedux: (state, action: PayloadAction<boolean>) => {
      state.spaceValidationRun.active = action.payload;
    },
    setSpaceValidationsResultsInRedux: (
      state,
      action: PayloadAction<ISpaceNumberAndSpaceValidationsResults[]>
    ) => {
      state.spaceValidationRun.spacesValidationsResults = action.payload;
    },
    resetSpaceValidationRunInRedux: (state) => {
      state.spaceValidationRun = {
        active: false,
        loading: false,
        spacesValidationsResults: [],
      };
    },
    setSpaceValidationLoadingInRedux: (state, action: PayloadAction<boolean>) => {
      state.spaceValidationRun.loading = action.payload;
    },
    toggleSpaceValidationSpecInRedux: (state, action: PayloadAction<SPACE_VALIDATION_ID_ENUM>) => {
      state.activeSpaceValidationSpecs[action.payload] =
        !state.activeSpaceValidationSpecs[action.payload];
    },
    setActiveColorPanelItemInRedux: (
      state,
      action: PayloadAction<COLOR_FILTERS_IDS | string | undefined>
    ) => {
      state.activeColorPanelItem = action.payload;
    },
    setFreeTextSearchInRedux: (state, action: PayloadAction<string>) => {
      state.freeTextSearch = action.payload;
    },
    setShowFireDeviceCircles: (state, action: PayloadAction<boolean>) => {
      state.showFireDeviceCircles = action.payload;
    },
    setShowFireDeviceSquare: (state, action: PayloadAction<boolean>) => {
      state.showFireDeviceSquare = action.payload;
    },
    setDeviceOnTheMoveInRedux: (state, action: PayloadAction<IObjectId | undefined>) => {
      state.deviceOnTheMove = action.payload;
    },
    setMeasurementToolActiveInRedux: (state, action: PayloadAction<boolean>) => {
      state.measurementToolActive = action.payload;
    },
    setProjectNoInRedux: (state, action: PayloadAction<number>) => {
      state.projectNo = action.payload;
    },
    addNonPublischedDevicesFromAlgosInRedux: (state, action: PayloadAction<IDevice[]>) => {
      state.nonPublischedDevicesFromAlgos.push(...action.payload);
    },
    removeNonPublischedDevicesFromAlgosInRedux: (state, action: PayloadAction<string[]>) => {
      state.nonPublischedDevicesFromAlgos = state.nonPublischedDevicesFromAlgos.filter(
        (device) => !action.payload.includes(device._id as string)
      );
    },
    clearNonPublischedDevicesFromAlgosInRedux: (state) => {
      state.nonPublischedDevicesFromAlgos = [];
    },
    setDeviceViewModeInRedux: (state, action: PayloadAction<IDeviceViewMode>) => {
      state.deviceViewMode = action.payload;
      localStorage.setItem("deviceViewMode", action.payload);
    },
    setBuildingStoryListRedux: (state, action: PayloadAction<IBuildingStoryListState>) => {
      state.buildingStoryList = action.payload;
    },
    setActiveModuleInRedux: (state, action: PayloadAction<IActiveModule>) => {
      localStorage.setItem("module", action.payload?.toString() ?? "");
      state.activeModule = action.payload;
    },
    setShowColorFilterLargeInRedux: (state, action: PayloadAction<boolean>) => {
      state.showColorFilterLarge = action.payload;
    },
    setRfpStandardRoomDisciplineInRedux: (
      state,
      action: PayloadAction<IRfpStandardRoomDiscipline>
    ) => {
      state.rfpStandardRoomDiscipline = action.payload;
    },
    setRequestZoomExtentInRedux(state) {
      state.zoomExtentNonce += 1;
    },
    resetZoomExtentNonceInRedux(state) {
      state.zoomExtentNonce = 0;
    },
    setMouseHelpModalOpenInRedux(state, action: PayloadAction<boolean>) {
      state.mouseHelpModalOpen = action.payload;
    },
  }),
  selectors: {
    activeTab: (viewer) => viewer.activeTab,
  },
});

export const selectHttpProject = (state: IRootState) => {
  const projectNo = selectProjectNo(state);
  return openapi.endpoints.getMultiplacerProjectsSingleProject.select({ projectNo })(state).data;
};

export const selectCompCodePath = createSelector(
  [selectHttpProject],
  (httpProject): string | undefined => {
    return httpProject?.compCodePath;
  }
);

export const selectDragSelectSpaces = (state: IRootState) => state.design.dragSelectSpaces;
const selectedRooms = (state: IRootState) => state.design.selectedRooms;
const copySpaceModeActive = (state: IRootState) => state.design.copySpaceModeActive;
const spaceValidationRun = (state: IRootState) => state.design.spaceValidationRun;
const activeSpaceValidationSpecs = (state: IRootState) => state.design.activeSpaceValidationSpecs;
const activeColorPanelItem = (state: IRootState) => state.design.activeColorPanelItem;
const selectDeviceIdOnTheMove = (state: IRootState) => state.design.deviceOnTheMove;
const selectNonPublischedDevicesFromAlgos = (state: IRootState) =>
  state.design.nonPublischedDevicesFromAlgos;

export const selectMcBimSpacesPixPolygons = createSelector(
  [
    selectedSpaceNumber, // 1
    selectDragSelectSpaces, // 2
    selectedRooms, // 3
    copySpaceModeActive, // 4
    spaceValidationRun, // 5
    activeSpaceValidationSpecs, // 6
    activeColorPanelItem, // 7
    selectDeviceIdOnTheMove, // 10
    selectNonPublischedDevicesFromAlgos, // 11
    selectHttpMcbimSpacesResult, // 12
    selectActiveSingleRoomSummary, // 13
    selectHttpDevicesInRoom, // 14
    selectHttpMcBimItemsInActiveStandardRoom, // 15
    selectHttpGetRoomsCurrentFloorResult, // 16
    selectCompCodePath, // 17
    selectMcBimSpaceFromSelectedRoomNr, // 18
    selectGetColorFilters,
    selectMcbimSpaceHitSetByFreeTextSearch,
  ],
  (
    mcbimSpaceNumber, // 1
    dragSelectSpaces, // 2
    selectedRooms, // 3
    copySpaceModeActive, // 4
    spaceValidationRun, // 5
    activeSpaceValidationSpecs, // 6
    activeColorPanelItem, // 7
    deviceIdOnTheMove, // 10
    nonPublischedDevicesFromAlgos, // 11
    mcbimSpaces, // 12
    singleRoom, // 13
    devicesInRoom: IDevice[], // 14
    mcbimItemsInStandardSpace: IMcBimItem[], // 15
    rooms, // 16
    compCodePath, // 17
    singleSpace, // 18
    colorFilters,
    mcbimSpaceHitSetByFreeTextSearch
  ) => {
    const _mcbimSpaces = mcbimSpaces ?? [];
    const activeMcBimSpace = _mcbimSpaces.find((space) => space.name === mcbimSpaceNumber);

    const uniqueSpaceNumbersInNonPublishedDevices = new Set(
      nonPublischedDevicesFromAlgos.map((device) => device.roomNumber)
    );

    const deviceOnTheMove = devicesInRoom.find((device) => device._id === deviceIdOnTheMove);

    return _mcbimSpaces.reduce((acc, mcbimSpace) => {
      if (mcbimSpace.longName?.toUpperCase() === "AREA" || mcbimSpace.polygon == null) {
        return acc;
      }

      const room = rooms?.[mcbimSpace.name];
      let pixPolygon = convertMcBimSpaceToPixPolygon(mcbimSpace, dragSelectSpaces);

      if (pixPolygon != null) {
        if (room?.multiplacerCeilingDevicesLocked?.bool === true) {
          pixPolygon.text.color = "red";
        }

        let spaceIsValid: boolean | undefined;

        if (mcbimSpaceNumber != null && singleRoom != null && singleSpace != null) {
          if (mcbimSpaceNumber === mcbimSpace.name) {
            const spaceValidations = getValidationsForSpace({
              singleRoom: singleRoom,
              singleSpace: singleSpace,
              devicesInRoom: devicesInRoom,
              mcbimItemsInStandardSpace: mcbimItemsInStandardSpace,
              activeSpaceValidationSpecs,
              compCodePath: compCodePath as string,
            });

            spaceIsValid =
              spaceValidations.filter((validation) => validation.status === "WARNING").length === 0;

            // tjock kant när man står i space-valideringsläge
          }
        }

        // 🔹 NYTT: hämta både fill + stroke från färglogiken
        const polygonColors = getPolygonColorsOfPixPolygonSpace({
          theSpaceNumber: mcbimSpace.name,
          activeSpace:
            activeMcBimSpace != null
              ? {
                  spaceNumber: activeMcBimSpace.name,
                  spaceLongName: activeMcBimSpace.longName as string,
                }
              : null,
          mcbimSpacesNameAndLongName: _mcbimSpaces.map((space) => ({
            spaceNumber: space.name,
            spaceLongName: space.longName as string,
          })),
          selectedSpaces: selectedRooms,
          spaceValid: spaceIsValid,
          copySpaceModeActive,
          devicesExistsInSpace: devicesInRoom.length > 0,
          spaceValidationRun,
          activeColorPanelItem,
          room,
          space: mcbimSpace,
          freeTextSearchHit: mcbimSpaceHitSetByFreeTextSearch?.has(mcbimSpace.name) ?? false,
          deviceOnTheMove,
          nonPublischedDevicesFromAlgosSpaceNumbers: Array.from(
            uniqueSpaceNumbersInNonPublishedDevices
          ),
          colorFilters,
        });

        if (polygonColors.fill !== undefined) {
          pixPolygon.fillColor = polygonColors.fill as string;
        }

        if (polygonColors.stroke !== undefined) {
          pixPolygon.strokeColor = polygonColors.stroke;
          pixPolygon.strokeWidth = "20";
        } else {
          //  pixPolygon.strokeWidth = "10";
          pixPolygon.strokeColor = "#000000";
        }

        if (activeMcBimSpace?.name === mcbimSpace.name) {
          pixPolygon.strokeColor = "#8cb9efff";
          pixPolygon.strokeWidth = "15";
        }
      }

      if (pixPolygon !== undefined) {
        acc.push(pixPolygon);
      }

      return acc;
    }, [] as IPixPolygon[]);
  }
);

// export const selectAirTerminalDirectionViewerPathsFromMlResults = createSelector(
//   [selectMlResults],
//   (mlResults): IPixPath[] => {
//     return getPixPathsAirTerminalDirectionsFromMlResults(mlResults ?? []);
//   }
// );

export const selectPixPathsFromAdjustedCalculatedGrids = createSelector(
  [
    (
      state: IRootState,
      spacesAndRooms: {
        rooms: IRoomsSummaryHashmap;
        mcbimSpaces: PartialMcBimSpace[];
      }
    ) => spacesAndRooms,
  ],
  (spacesAndRooms): IPixPath[] => {
    const ceilingGrids = getCalculatedCeilingGrids(
      spacesAndRooms.mcbimSpaces,
      spacesAndRooms.rooms
    );
    const lines = ceilingGrids.flatMap((grid) => grid.gridLines);
    if (lines === undefined) {
      throw new Error("Ceiling grid lines undefined");
    }
    return lines.map((line) => {
      return {
        id: getRandomNrBetween(0, 1000000).toString(),
        strokeColor: "#5c5c5c",
        points: line as [[number, number], [number, number]],
        strokeWidth: "1",
      };
    });
  }
);

export const selectMlGenericPredicetedDevices = createSelector(
  [(state: IRootState) => state.design.mlGenrericSpaceDevices],
  (mlGenrericSpaceDevices): IPixPolygon[] => {
    return mlGenrericSpaceDevices.map((mlDevice) => {
      return convertGenereicMlResultToPixPolygon(mlDevice);
    });
  }
);

// selectors
const selectProjectNo = (state: IRootState) => state.design.projectNo;
const selectFloorAndJob = (state: IRootState) => state.design.floorAndJob;

export const selectGetDevicesArgs = createSelector(
  [selectProjectNo, selectFloorAndJob],
  (projectNo, floorAndJob): PostMultiplacerDevicesApiArg | null => {
    if (!floorAndJob?.floorName) {
      return null;
    }
    return {
      body: {
        projectNo,
        floor: floorAndJob.floorName as string,
        // om du senare vill stödja arrayer:
        // roomNumbers: fileFloorJob.roomNumbers ?? [],
      },
    };
  }
);

export const selectCeilingGridPixPaths = createSelector(
  [selectHttpMcbimSpacesResult, selectHttpGetRoomsCurrentFloorResult],
  (mcbimSpaces, rooms): IPixPath[] => {
    const ceilingGrids = getCalculatedCeilingGrids(mcbimSpaces ?? [], rooms ?? {});
    const lines = ceilingGrids.flatMap((grid) => grid.gridLines);
    if (lines === undefined) {
      throw new Error("Ceiling grid lines undefined");
    }
    return lines.map((line) => {
      return {
        id: getRandomNrBetween(0, 1000000).toString(),
        strokeColor: "#5c5c5c",
        points: line as [[number, number], [number, number]],
        strokeWidth: "1",
      };
    });
  }
);

// /** Hämtar query-resultatet (senaste i cachen) för nuvarande args */
// export const select = (state: IRootState) => {
//   const args = selectGetRoomsArgs(state);
//   if (!args) return undefined;
//   return openapi.endpoints.postMultiplacerRooms.select(args)(state);
// };

// export const selectIfcDevicesPixPolygons = createSelector([], ():IPixPolygon => {
//   return [];
// }

// Action creators are generated for each case reducer function.
export const {
  setCountAlgoSpaceTotal,
  setCountAlgoSpaceComplete,
  setCountAlgoSpaceFailed,
  setAlgosInProgressInRedux,
  setHidePlayButtonInRedux,
  setToastInRedux,
  setViewerToBeShowInRedux,
  setSelectedSpaceNumberInRedux,
  setSelectedRoomTabInRedux,
  setSelectedSpaceValidationInRedux,
  setActiveLeftSideBarTabInRedux,
  setDragSelectSpacesInRedux,
  setSelectedRoomsInRedux,
  addSelectedRoomToRedux,
  setShiftSelectRoomsInRedux,
  removeSelectedRoomFromRedux,
  toggleReviewModeInRedux,
  incCurrIndexReviewModeInRedux,
  decCurrIndexReviewModeInRedux,
  resetReviewModeInRedux,
  setIfcClassesToDisplayInRedux,
  setDeviceTypeInstanceClassFilterInRedux,
  setRotationInRedux,
  setCopySpaceModeActiveInRedux,
  toggleShowTrueSizeOfDevicesInRedux,
  toggleSnapToGridInRedux,
  setSpaceValidationsResultsInRedux,
  resetSpaceValidationRunInRedux,
  setSpaceValidationRunActiveInRedux,
  setSpaceValidationLoadingInRedux,
  toggleSpaceValidationSpecInRedux,
  setMultipleSelectedRoomsToRedux,
  setActiveColorPanelItemInRedux,
  setFreeTextSearchInRedux,
  setShowFireDeviceCircles,
  setDeviceOnTheMoveInRedux,
  setMeasurementToolActiveInRedux,
  setProjectNoInRedux,
  addNonPublischedDevicesFromAlgosInRedux,
  clearNonPublischedDevicesFromAlgosInRedux,
  removeNonPublischedDevicesFromAlgosInRedux,
  setDeviceViewModeInRedux,
  setFloorAndJobInRedux,
  setBuildingStoryListRedux,
  setActiveModuleInRedux,
  setRfpStandardRoomDisciplineInRedux,
  setShowColorFilterLargeInRedux,
  setRequestZoomExtentInRedux,
  resetZoomExtentNonceInRedux,
  setMouseHelpModalOpenInRedux,
} = designSlice.actions;
